// ---------------------------------------------------------------------------
// ChartCard -- Universal Chart Container
// ---------------------------------------------------------------------------
// Wraps any chart in a consistent V11 card with:
//   - Title + optional subtitle
//   - Content area (children)
//   - Optional legend slot (rendered below content)
// ---------------------------------------------------------------------------

import React from 'react'

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface ChartCardProps {
  /** Card title */
  readonly title?: string
  /** Optional subtitle (e.g., "Based on 2,760 profiles") */
  readonly subtitle?: string
  /** Chart content */
  readonly children: React.ReactNode
  /** Optional legend rendered below the chart */
  readonly legend?: React.ReactNode
  /** Additional CSS class on the card container */
  readonly className?: string
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export const ChartCard: React.FC<ChartCardProps> = ({
  title,
  subtitle,
  children,
  legend,
  className = '',
}) => {
  return (
    <div
      data-testid="chart-card"
      className={`rounded-xl border border-v11-border bg-v11-surface p-5 h-full flex flex-col ${className}`}
    >
      {/* Header */}
      {title && (
        <div className="mb-4 flex-shrink-0">
          <h3 className="text-sm font-semibold text-v11-text-primary">
            {title}
          </h3>
          {subtitle && (
            <p className="mt-1 text-xs text-v11-text-muted">{subtitle}</p>
          )}
        </div>
      )}

      {/* Chart Content - centered vertically in available space */}
      <div className="w-full flex-1 flex items-center">
        <div className="w-full">{children}</div>
      </div>

      {/* Legend Slot */}
      {legend && <div className="mt-4 flex-shrink-0">{legend}</div>}
    </div>
  )
}
